import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";

import { FormField } from "components/form/FormField/FormField";
import { Button } from "components/ui/Button";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { LetterTab } from "features/consultations/components/tabs/LetterTab";
import { PrescriptionTab } from "features/consultations/components/tabs/PrescriptionTab";
import { ReferralTab } from "features/consultations/components/tabs/ReferralTab";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { SendMncmsButton } from "features/pregnancy/components/antenatal/SendMncmsButton/SendMncmsButton";
import { PostnatalBabyObservationSection } from "features/pregnancy/components/PostnatalBabyObservationSection/PostnatalBabyObservationSection";
import { CONSULTATION_TAB_OPTIONS } from "features/pregnancy/constants/consultationTabOptions";
import { useMaternityHospitals } from "features/pregnancy/hooks/useMaternityHospitals";
import { usePostnatalConsultationSave } from "features/pregnancy/hooks/usePostnatalConsultationSave";
import { formatConsultationDate } from "features/pregnancy/utils/formatConsultationDate";
import { getMissingPostnatalMncmsFields } from "features/pregnancy/utils/getMissingMncmsFields";
import { getPostnatalDefaultValues } from "features/pregnancy/utils/getPostnatalDefaultValues";
import { describeMncmsDestination } from "features/pregnancy/utils/receivingHospital";

import { formSchema } from "../PregnancyPostNatalView/formSchema";
import style from "./PregnancyPostNatalConsultationForm.module.css";

// MN-CMS messaging is not yet implemented on the backend for postnatal visits
// (antenatal only — there's no postnatalconsultation/{id}/send-mncms-* endpoint).
// Keep the send/correction action hidden until those endpoints exist.
const IS_POSTNATAL_MNCMS_ENABLED = false;

export const PregnancyPostNatalConsultationForm = ({
  closeView,
  tenantVal,
  pregnancyId,
  patientId,
  postNatalConsultation,
  pregnancy,
}) => {
  const isEditMode = !!postNatalConsultation;
  const [activeTab, setActiveTab] = useState("consultation");

  const userName = localStorage.getItem("kinesin-name");
  const consultationDisplayDate = formatConsultationDate(postNatalConsultation?.date);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: getPostnatalDefaultValues(postNatalConsultation),
  });

  const { data: patient } = usePatientData(patientId);
  const missingMncmsFields = getMissingPostnatalMncmsFields(pregnancy, patient);

  // The hospital this pregnancy's messages actually go to — MSH.6, not the consultant's
  // hospital (KIN-2031).
  const { data: maternityHospitals } = useMaternityHospitals();
  const destinationName = describeMncmsDestination(pregnancy, maternityHospitals);

  const {
    consultationId,
    foetuses,
    babyObservations,
    babyErrors,
    updateBaby,
    wasSent,
    sendError,
    isPending,
    isSendPending,
    handleFinish,
    handleFinishAndSend,
    handleRetrySend,
  } = usePostnatalConsultationSave({
    tenantVal,
    patientId,
    pregnancyId,
    postNatalConsultation,
    pregnancy,
    form,
    closeView,
  });

  const isMultiple = foetuses.length > 1;

  return (
    <div className={style.wrapper}>
      {/* ── Green header ── */}
      <div className={style.consultationHeader}>
        <div className={style.headerLeft}>
          <button
            type="button"
            className={style.backButton}
            aria-label="Back"
            onClick={closeView}
          >
            <ArrowLeft />
          </button>
          <div className={style.headerInfo}>
            <p className={style.headerLabel}>Post-natal Visit</p>
            <p className={style.headerDate}>{consultationDisplayDate}</p>
          </div>
        </div>
        <div className={style.headerActions}>
          {isEditMode ? (
            <>
              <Button type="button" onClick={handleFinish} disabled={isPending}>
                Save consultation
              </Button>
              {IS_POSTNATAL_MNCMS_ENABLED && !!consultationId && (
                <SendMncmsButton
                  destinationName={destinationName}
                  consultationId={consultationId}
                  isSent={wasSent}
                  isSendPending={isSendPending || isPending}
                  sendError={sendError}
                  isSendReady={missingMncmsFields.length === 0}
                  missingFields={missingMncmsFields}
                  onSend={handleFinishAndSend}
                  onRetry={handleRetrySend}
                />
              )}
            </>
          ) : (
            <>
              <Button type="button" onClick={handleFinish} disabled={isPending}>
                Finish consultation
              </Button>
              {IS_POSTNATAL_MNCMS_ENABLED && (
                <SendMncmsButton
                  destinationName={destinationName}
                  consultationId={consultationId}
                  isSent={wasSent}
                  isSendPending={isSendPending || isPending}
                  sendError={sendError}
                  isSendReady={missingMncmsFields.length === 0}
                  missingFields={missingMncmsFields}
                  sendActionLabel="Finish, and send"
                  onSend={handleFinishAndSend}
                  onRetry={handleRetrySend}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Tab content area ── */}
      <div className={style.tabContentArea}>
        <div className={style.tabSwitcherWrapper}>
          <TabSwitcher
            options={CONSULTATION_TAB_OPTIONS}
            value={activeTab}
            onChange={setActiveTab}
            fullWidth
          />
        </div>

        {activeTab === "consultation" && (
          <div className={style.tabBody}>
            {/* Read-only top fields */}
            <div className={style.inputGroupGrid}>
              <div>
                <label className={style.pregnancyFormLabel}>
                  Consultation type
                </label>
                <input
                  readOnly
                  className={`form-control ${style.readOnlyInput}`}
                  value="Post-natal Visit"
                />
              </div>
              <div>
                <label className={style.pregnancyFormLabel}>
                  Date and time
                </label>
                <input
                  readOnly
                  className={`form-control ${style.readOnlyInput}`}
                  value={consultationDisplayDate}
                />
              </div>
              <div>
                <label className={style.pregnancyFormLabel}>Doctor</label>
                <input
                  readOnly
                  className={`form-control ${style.readOnlyInput}`}
                  value={userName ?? ""}
                />
              </div>
            </div>

            {/* Editable form */}
            <FormProvider {...form}>
              <form
                className={style.consultationForm}
                onSubmit={handleFinish}
              >
                {/* One assessment per baby (KIN-1075) — Subject + Objective. */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "24px",
                    marginBottom: "12px",
                  }}
                >
                  {foetuses.map((foetus) => (
                    <PostnatalBabyObservationSection
                      key={foetus.foetusIdentifier}
                      foetusIdentifier={isMultiple ? foetus.foetusIdentifier : null}
                      values={babyObservations[foetus.foetusIdentifier] ?? {}}
                      errors={babyErrors[foetus.foetusIdentifier]}
                      onChange={(updated) =>
                        updateBaby(foetus.foetusIdentifier, updated)
                      }
                      formStyle={style}
                    />
                  ))}
                </div>

                <h3 className={style.pregnancyFormSectionTitle}>Mother</h3>
                <div className={style.inputGroupGrid}>
                  <FormField name="gravida">
                    <label className={style.pregnancyFormLabel}>Gravida <span className={style.requiredMark}>*</span></label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="form-control"
                      placeholder="e.g. 2"
                      onInput={(e) =>
                        (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                      }
                      {...form.register("gravida")}
                    />
                  </FormField>
                  <FormField name="para">
                    <label className={style.pregnancyFormLabel}>Para <span className={style.requiredMark}>*</span></label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="form-control"
                      placeholder="e.g. 1"
                      onInput={(e) =>
                        (e.target.value = e.target.value.replace(/[^0-9]/g, ""))
                      }
                      {...form.register("para")}
                    />
                  </FormField>
                  <FormField name="postNatalMood">
                    <label className={style.pregnancyFormLabel}>
                      Post-natal mood <span className={style.requiredMark}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Positive, bonding well"
                      {...form.register("postNatalMood")}
                    />
                  </FormField>
                  <FormField name="supports">
                    <label className={style.pregnancyFormLabel}>Supports <span className={style.requiredMark}>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Partner and family support present"
                      {...form.register("supports")}
                    />
                  </FormField>
                  <FormField name="contraception">
                    <label className={style.pregnancyFormLabel}>Contraception <span className={style.requiredMark}>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Pill, IUCD discussed"
                      {...form.register("contraception")}
                    />
                  </FormField>
                  <FormField name="mirena">
                    <label className={style.pregnancyFormLabel}>Mirena <span className={style.requiredMark}>*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. To be inserted at 6-week check"
                      {...form.register("mirena")}
                    />
                  </FormField>
                </div>
                <FormField name="issues">
                  <label className={style.pregnancyFormLabel}>Issues <span className={style.requiredMark}>*</span></label>
                  <textarea
                    className="form-control textareaform-control"
                    style={{ minHeight: "120px" }}
                    placeholder="e.g. Perineal tear healing well, mild fatigue reported"
                    {...form.register("issues")}
                  />
                </FormField>
              </form>
            </FormProvider>
          </div>
        )}

        {activeTab === "referrals" && (
          <div className={style.externalTabBody}>
            <ReferralTab
              consultationId={consultationId}
              formWrapperStyle={{
                padding: 0,
                border: "1px solid var(--gray-200)",
                borderRadius: "12px",
              }}
              resetOnSuccess
              showAllReferrals
            />
          </div>
        )}

        {activeTab === "letters" && (
          <div className={style.externalTabBody}>
            {/* consultationId intentionally null — pregnancy consultations are a separate
                entity from general consultations; the BE cannot link them and throws if
                a pregnancy consultationId is passed to the letter endpoint */}
            <LetterTab consultationId={null} />
          </div>
        )}

        {activeTab === "prescriptions" && (
          <div className={style.externalTabBody}>
            <PrescriptionTab consultationId={consultationId} resetOnSuccess />
          </div>
        )}
      </div>

    </div>
  );
};
